import { ITourPackage } from '@appTypes/redefined-types';

/**
 * Docs flows/tour-package.md R-TD7 / R-TD8 (Amiran 09.10.2026), QA doc processes/tour-search-and-checkout-totals.md R-LD7 and features/website/cart.md R-C33 / R-C34.
 * The hotel stay of a tour whose flight lands 2+ calendar days after take-off is shorter than the trip: the row and the cart say "N of M nights" and, when the
 * stay starts the evening before the landing, show the chip "Evening before". Computed from what the package already carries (hotel dates, the flight's
 * segments) - no new API field. Calendar days come from the date part ("yyyy-MM-dd") only: the times are the airports' local times, no time zone is applied.
 */
export type TourStayStart = 'LandingDay' | 'EveningBefore';

export interface TourStayLabel {
  checkIn: string;
  checkOut: string;
  hotelNights: number;
  tripNights: number;
  start: TourStayStart;
  /** The landing of the last outbound segment, as sent (airport local time). */
  landing: string;
  /** Calendar days between the first take-off and the last outbound landing (the "+N" badge). */
  arrivalDays: number;
}

const datePart = (value: unknown): string | null => {
  const text = value === null || value === undefined ? '' : String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : null;
};

const dayNumber = (date: string): number => {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
};

/** Calendar days from `earlier` to `later` by their date part; null when either is not a date. */
export function calendarDaysBetween(earlier: unknown, later: unknown): number | null {
  const a = datePart(earlier);
  const b = datePart(later);
  return a && b ? dayNumber(b) - dayNumber(a) : null;
}

/** The label of a landing-day / evening-before stay, or null for a stay that starts on the take-off day (nothing to say). */
export function tourStayLabel(pkg: ITourPackage | null | undefined): TourStayLabel | null {
  const out = pkg?.flight?.departureSegments ?? [];
  const takeOff = out.at(0)?.departureFlightDate;
  const landing = out.at(-1)?.arrivalFlightDate;
  const returnTakeOff = pkg?.flight?.returnSegments?.at(0)?.departureFlightDate;
  const checkIn = datePart(pkg?.hotel?.checkInDate);
  const checkOut = datePart(pkg?.hotel?.checkOutDate);
  const takeOffDay = datePart(takeOff);
  const landingDay = datePart(landing);
  if (!checkIn || !checkOut || !takeOffDay || !landingDay || checkIn === takeOffDay) return null;

  const tripNights = calendarDaysBetween(takeOff, returnTakeOff ?? checkOut) ?? 0;
  return {
    checkIn,
    checkOut,
    hotelNights: pkg?.hotel?.nights ?? dayNumber(checkOut) - dayNumber(checkIn),
    tripNights,
    start: dayNumber(checkIn) < dayNumber(landingDay) ? 'EveningBefore' : 'LandingDay',
    landing: String(landing),
    arrivalDays: dayNumber(landingDay) - dayNumber(takeOffDay)
  };
}
